import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { apiClient, sendQuoteEmail, sendInvoiceEmail, ApiError } from './client';
import type {
  UploadResponse,
  ProcessQuoteResponse,
  UpdateQuoteInput,
  QuoteListResponse,
  UpdateStatusResponse,
  QuoteStatus,
  JobResponse,
  CreateJobResponse,
  JobPrepResponse,
  QuoteScoreResponse,
  EmailDraftRequest,
  EmailDraftResponse,
  ClientListResponse,
  ClientDetail,
  CreateClientInput,
  UpdateClientInput,
  AccountDeleteResponse,
  AccountExportResponse,
  QuoteRequest,
  QuoteRequestListResponse,
  QuoteRequestStatus,
  SubmitQuoteRequestInput,
  SubmitQuoteRequestResponse,
  ConvertQuoteRequestResponse,
  AnalyticsResponse,
  ShareQuoteResponse,
  SharedQuoteView,
  RespondToQuoteInput,
  PriceCheckResponse,
  Invoice,
  InvoiceListResponse,
  UpdatePaymentResponse,
  PaymentStatus,
  ContractorProfile,
  ContractorProfileFull,
  ContractorProfileInput,
} from './types';

function getApiUrl(): string {
  return import.meta.env.VITE_API_URL as string;
}

interface UploadInput {
  blob: Blob;
  mimeType: string;
}

interface ProcessQuoteInput {
  file_key: string;
  language?: string;
}

interface ProcessTextInput {
  text: string;
  language?: string;
}

export type GetAccessToken = () => Promise<string>;

export function useUpload(getAccessToken: GetAccessToken) {
  return useMutation<UploadResponse, Error, UploadInput>({
    mutationFn: async ({ blob, mimeType }) => {
      const token = await getAccessToken();
      const formData = new FormData();
      const ext = mimeType.split('/').pop()!.split(';')[0] || 'webm';
      formData.append('file', blob, `recording.${ext}`);

      return apiClient<UploadResponse>('/upload', {
        method: 'POST',
        body: formData,
        token,
      });
    },
  });
}

export function useProcessQuote(getAccessToken: GetAccessToken) {
  return useMutation<ProcessQuoteResponse, Error, ProcessQuoteInput>({
    mutationFn: async ({ file_key, language }) => {
      const token = await getAccessToken();

      return apiClient<ProcessQuoteResponse>('/process-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_key, language }),
        token,
      });
    },
  });
}

export function useProcessText(getAccessToken: GetAccessToken) {
  return useMutation<ProcessQuoteResponse, Error, ProcessTextInput>({
    mutationFn: async ({ text, language }) => {
      const token = await getAccessToken();
      return apiClient<ProcessQuoteResponse>('/process-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language }),
        token,
      });
    },
  });
}

export interface ConfidenceFilter {
  min_confidence?: number;
  max_confidence?: number;
}

export function useQuotes(
  getAccessToken: GetAccessToken,
  sort: 'newest' | 'oldest' = 'newest',
  confidenceFilter?: ConfidenceFilter,
  search?: string,
  statusFilter?: string,
  limit: number = 20,
) {
  return useInfiniteQuery<QuoteListResponse>({
    queryKey: ['quotes', sort, confidenceFilter, search, statusFilter, limit],
    queryFn: async ({ pageParam }) => {
      const token = await getAccessToken();
      const params = new URLSearchParams({ limit: String(limit), sort });
      if (pageParam) params.set('cursor', pageParam as string);
      if (confidenceFilter?.min_confidence !== undefined) {
        params.set('min_confidence', String(confidenceFilter.min_confidence));
      }
      if (confidenceFilter?.max_confidence !== undefined) {
        params.set('max_confidence', String(confidenceFilter.max_confidence));
      }
      if (search) {
        params.set('search', search);
      }
      if (statusFilter) {
        params.set('status', statusFilter);
      }

      return apiClient<QuoteListResponse>(`/quotes?${params}`, {
        method: 'GET',
        token,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
}

export function useQuote(getAccessToken: GetAccessToken, id: string | null) {
  return useQuery<ProcessQuoteResponse>({
    queryKey: ['quote', id],
    queryFn: async () => {
      const token = await getAccessToken();
      return apiClient<ProcessQuoteResponse>(`/quotes/${id}`, {
        method: 'GET',
        token,
      });
    },
    enabled: id !== null,
  });
}

export function useUpdateQuote(getAccessToken: GetAccessToken) {
  return useMutation<ProcessQuoteResponse, Error, UpdateQuoteInput & { id: string }>({
    mutationFn: async ({ id, structured_data, client_id }) => {
      const token = await getAccessToken();
      const reqBody: Record<string, unknown> = { structured_data };
      if (client_id !== undefined) {
        reqBody.client_id = client_id;
      }

      return apiClient<ProcessQuoteResponse>(`/quotes/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody),
        token,
      });
    },
  });
}

export function useUpdateQuoteStatus(getAccessToken: GetAccessToken) {
  return useMutation<UpdateStatusResponse, Error, { id: string; status: QuoteStatus }>({
    mutationFn: async ({ id, status }) => {
      const token = await getAccessToken();
      return apiClient<UpdateStatusResponse>(`/quotes/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
        token,
      });
    },
  });
}

export function useDeleteQuote(getAccessToken: GetAccessToken) {
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const token = await getAccessToken();
      return apiClient<void>(`/quotes/${id}`, {
        method: 'DELETE',
        token,
      });
    },
  });
}

interface BulkDeleteResponse {
  deleted: number;
}

export function useBulkDeleteQuotes(getAccessToken: GetAccessToken) {
  return useMutation<BulkDeleteResponse, Error, string[]>({
    mutationFn: async (ids) => {
      const token = await getAccessToken();
      return apiClient<BulkDeleteResponse>('/quotes/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
        token,
      });
    },
  });
}

export function useJobPrep(getAccessToken: GetAccessToken) {
  return useMutation<
    JobPrepResponse,
    Error,
    { id: string; regenerate?: boolean; language?: string }
  >({
    mutationFn: async ({ id, regenerate, language }) => {
      const token = await getAccessToken();
      return apiClient<JobPrepResponse>(`/quotes/${id}/job-prep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: regenerate ?? false, language }),
        token,
      });
    },
  });
}

export function useQuoteScore(getAccessToken: GetAccessToken) {
  return useMutation<
    QuoteScoreResponse,
    Error,
    { id: string; regenerate?: boolean; language?: string }
  >({
    mutationFn: async ({ id, regenerate, language }) => {
      const token = await getAccessToken();
      return apiClient<QuoteScoreResponse>(`/quotes/${id}/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: regenerate ?? false, language }),
        token,
      });
    },
  });
}

export function usePriceCheck(getAccessToken: GetAccessToken) {
  return useMutation<
    PriceCheckResponse,
    Error,
    { id: string; regenerate?: boolean; language?: string }
  >({
    mutationFn: async ({ id, regenerate, language }) => {
      const token = await getAccessToken();
      return apiClient<PriceCheckResponse>(`/quotes/${id}/price-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: regenerate ?? false, language }),
        token,
      });
    },
  });
}

export function useGenerateEmailDraft(getAccessToken: GetAccessToken) {
  return useMutation<EmailDraftResponse, Error, { id: string } & EmailDraftRequest>({
    mutationFn: async ({ id, language, contractor }) => {
      const token = await getAccessToken();
      return apiClient<EmailDraftResponse>(`/quotes/${id}/email-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, contractor }),
        token,
      });
    },
  });
}

export function useSearchClients(getAccessToken: GetAccessToken, search: string, enabled: boolean) {
  return useQuery<ClientListResponse>({
    queryKey: ['clients', 'search', search],
    queryFn: async () => {
      const token = await getAccessToken();
      const params = new URLSearchParams({ search, limit: '5' });
      return apiClient<ClientListResponse>(`/clients?${params}`, {
        method: 'GET',
        token,
      });
    },
    enabled: enabled && search.length >= 2,
  });
}

export function useClient(getAccessToken: GetAccessToken, clientId: string | null) {
  return useQuery<ClientDetail>({
    queryKey: ['clients', clientId],
    queryFn: async () => {
      const token = await getAccessToken();
      return apiClient<ClientDetail>(`/clients/${clientId}`, {
        method: 'GET',
        token,
      });
    },
    enabled: !!clientId,
  });
}

export function useCreateClient(getAccessToken: GetAccessToken) {
  return useMutation<ClientDetail, Error, CreateClientInput>({
    mutationFn: async (input) => {
      const token = await getAccessToken();
      return apiClient<ClientDetail>('/clients', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
        token,
      });
    },
  });
}

export function useUpdateClient(getAccessToken: GetAccessToken) {
  return useMutation<ClientDetail, Error, { id: string } & UpdateClientInput>({
    mutationFn: async ({ id, ...input }) => {
      const token = await getAccessToken();
      return apiClient<ClientDetail>(`/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
        token,
      });
    },
  });
}

export function useDeleteClient(getAccessToken: GetAccessToken) {
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const token = await getAccessToken();
      await apiClient<void>(`/clients/${id}`, {
        method: 'DELETE',
        token,
      });
    },
  });
}

export function useDeleteAccount(getAccessToken: GetAccessToken) {
  return useMutation<AccountDeleteResponse, Error, void>({
    mutationFn: async () => {
      const token = await getAccessToken();
      return apiClient<AccountDeleteResponse>('/account', {
        method: 'DELETE',
        token,
      });
    },
  });
}

export function useExportAccount(getAccessToken: GetAccessToken) {
  return useMutation<AccountExportResponse, Error, void>({
    mutationFn: async () => {
      const token = await getAccessToken();
      return apiClient<AccountExportResponse>('/account/export', {
        method: 'GET',
        token,
      });
    },
  });
}

// Public — no auth token needed
export function useSubmitQuoteRequest() {
  return useMutation<
    SubmitQuoteRequestResponse,
    Error,
    { userId: string } & SubmitQuoteRequestInput
  >({
    mutationFn: async ({ userId, ...input }) => {
      return apiClient<SubmitQuoteRequestResponse>(`/intake/p/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
    },
  });
}

// Authenticated
export function useQuoteRequests(
  getAccessToken: GetAccessToken,
  status?: QuoteRequestStatus,
  limit: number = 20,
  enabled: boolean = true,
) {
  return useInfiniteQuery<QuoteRequestListResponse>({
    queryKey: ['quote-requests', status, limit],
    enabled,
    queryFn: async ({ pageParam }) => {
      const token = await getAccessToken();
      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam as string);
      if (status) params.set('status', status);
      return apiClient<QuoteRequestListResponse>(`/quote-requests?${params}`, {
        method: 'GET',
        token,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
}

export function useUpdateQuoteRequestStatus(getAccessToken: GetAccessToken) {
  return useMutation<QuoteRequest, Error, { id: string; status: QuoteRequestStatus }>({
    mutationFn: async ({ id, status }) => {
      const token = await getAccessToken();
      return apiClient<QuoteRequest>(`/quote-requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
        token,
      });
    },
  });
}

export function useDeleteQuoteRequest(getAccessToken: GetAccessToken) {
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const token = await getAccessToken();
      return apiClient<void>(`/quote-requests/${id}`, {
        method: 'DELETE',
        token,
      });
    },
  });
}

export function useConvertQuoteRequest(getAccessToken: GetAccessToken) {
  return useMutation<ConvertQuoteRequestResponse, Error, string>({
    mutationFn: async (id) => {
      const token = await getAccessToken();
      return apiClient<ConvertQuoteRequestResponse>(`/quote-requests/${id}/convert`, {
        method: 'POST',
        token,
      });
    },
  });
}

export function useAnalytics(getAccessToken: GetAccessToken, period: string) {
  return useQuery<AnalyticsResponse>({
    queryKey: ['analytics', period],
    queryFn: async () => {
      const token = await getAccessToken();
      return apiClient<AnalyticsResponse>(`/analytics?period=${period}`, {
        method: 'GET',
        token,
      });
    },
    staleTime: 60_000,
  });
}

export class PublicApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'PublicApiError';
  }
}

async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_URL;
  const response = await fetch(`${baseUrl}${path}`, options);
  if (!response.ok) {
    throw new PublicApiError(`Request failed: ${response.status}`, response.status);
  }
  return response.json() as Promise<T>;
}

export function useShareQuote(getAccessToken: GetAccessToken) {
  return useMutation<
    ShareQuoteResponse,
    Error,
    { quoteId: string; contractor: Record<string, unknown> }
  >({
    mutationFn: async ({ quoteId, contractor }) => {
      const token = await getAccessToken();
      return apiClient<ShareQuoteResponse>(`/quotes/${quoteId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractor }),
        token,
      });
    },
  });
}

export function useSharedQuote(shareToken: string) {
  return useQuery<SharedQuoteView>({
    queryKey: ['shared-quote', shareToken],
    queryFn: () => publicFetch<SharedQuoteView>(`/share/${shareToken}`),
    enabled: !!shareToken,
    retry: false,
  });
}

export function useRespondToQuote() {
  return useMutation<{ success: boolean }, Error, { token: string } & RespondToQuoteInput>({
    mutationFn: async ({ token, ...input }) => {
      return publicFetch<{ success: boolean }>(`/share/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
    },
  });
}

export function useCreateInvoice() {
  const { getToken } = useKindeAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      quoteId,
      paymentTermDays,
    }: {
      quoteId: string;
      paymentTermDays?: number;
    }): Promise<Invoice> => {
      const token = await getToken();
      const url = getApiUrl() + '/quotes/' + quoteId + '/invoice';
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          paymentTermDays === undefined ? {} : { payment_term_days: paymentTermDays },
        ),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error((data as { error: string }).error || 'Failed to create invoice');
      }
      return res.json() as Promise<Invoice>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useUpdatePayment() {
  const { getToken } = useKindeAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      paymentStatus,
      paymentMethod,
      paymentNotes,
    }: {
      invoiceId: string;
      paymentStatus: PaymentStatus;
      paymentMethod?: string;
      paymentNotes?: string;
    }): Promise<UpdatePaymentResponse> => {
      const token = await getToken();
      const res = await fetch(`${getApiUrl()}/invoices/${invoiceId}/payment`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_status: paymentStatus,
          ...(paymentMethod && { payment_method: paymentMethod }),
          ...(paymentNotes && { payment_notes: paymentNotes }),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error((data as { error: string }).error || 'Failed to update payment');
      }
      return res.json() as Promise<UpdatePaymentResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useInvoices(status?: PaymentStatus) {
  const { getToken } = useKindeAuth();

  return useInfiniteQuery({
    queryKey: ['invoices', status],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (pageParam) params.set('cursor', pageParam);
      const query = params.toString();
      const url = getApiUrl() + '/invoices' + (query ? '?' + query : '');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch invoices');
      return res.json() as Promise<InvoiceListResponse>;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: InvoiceListResponse) => lastPage.next_cursor ?? undefined,
  });
}

export function useSaveProfile(getAccessToken: GetAccessToken) {
  return useMutation<ContractorProfileFull, Error, ContractorProfileInput>({
    mutationFn: async (input) => {
      const token = await getAccessToken();
      return apiClient<ContractorProfileFull>('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        token,
      });
    },
  });
}

export function useOwnProfile(getAccessToken: GetAccessToken, userId?: string) {
  return useQuery<ContractorProfileFull | null>({
    queryKey: ['own-profile', userId ?? 'unknown'],
    queryFn: async () => {
      const token = await getAccessToken();
      try {
        return await apiClient<ContractorProfileFull>('/profile', { token });
      } catch (err) {
        // 404 = no profile yet (new user) — return null
        if (err instanceof ApiError && err.status === 404) return null;
        // Other errors (401, 500, network) should propagate
        throw err;
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useContractorProfile(publicId: string) {
  return useQuery<ContractorProfile | null>({
    queryKey: ['contractor-profile', publicId],
    queryFn: async () => {
      try {
        return await publicFetch<ContractorProfile>('/p/' + publicId);
      } catch {
        return null;
      }
    },
    enabled: !!publicId,
    staleTime: 300_000,
    retry: false,
  });
}

export function useSendQuoteEmail(getAccessToken: GetAccessToken) {
  return useMutation<
    { success: true },
    Error,
    { quoteId: string; to: string; message?: string; subject?: string }
  >({
    mutationFn: async ({ quoteId, to, message, subject }) => {
      const token = await getAccessToken();
      return sendQuoteEmail(token, quoteId, { to, message, subject });
    },
  });
}

export function useSendInvoiceEmail(getAccessToken: GetAccessToken) {
  return useMutation<
    { success: true },
    Error,
    { invoiceId: string; to: string; message?: string; subject?: string }
  >({
    mutationFn: async ({ invoiceId, to, message, subject }) => {
      const token = await getAccessToken();
      return sendInvoiceEmail(token, invoiceId, { to, message, subject });
    },
  });
}

// --- Async job hooks ---

export function useCreateJob(getAccessToken: GetAccessToken) {
  return useMutation<CreateJobResponse, Error, { file_key: string; language?: string }>({
    mutationFn: async (data) => {
      const token = await getAccessToken();
      return apiClient<CreateJobResponse>('/jobs/process-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        token,
      });
    },
  });
}

export function useJobStatus(getAccessToken: GetAccessToken, jobId: string | null) {
  return useQuery<JobResponse>({
    queryKey: ['job', jobId],
    queryFn: async () => {
      const token = await getAccessToken();
      return apiClient<JobResponse>(`/jobs/${jobId}`, { token });
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 2000;
    },
  });
}
